// ─── BattleViewer2D – Event Applicator ──────────────────────────────────────

import {
  AnimEvent, Sprite, DamageFloat, Particle, CameraState, LogEntry, LogRound,
  CIRCLE_RADIUS, CANVAS_H, DAMAGE_EVENTS, ATTACK_EVENTS,
} from './types';
import { getRacialFloatLabel } from './utils';
import {
  spawnImpactBurst, spawnSparkBurst, spawnBleedDrip, spawnHealParticles,
} from './particles';

export interface ApplyEventContext {
  sprites: Map<string, Sprite>;
  dmgFloats: DamageFloat[];
  dmgFloatId: { current: number };
  particles: Particle[];
  camera: CameraState;
  logRounds: LogRound[];
  pendingEntries: LogEntry[];
  currentRound: { current: LogRound | null };
  entryId: { current: number };
  setCurrentRound: (r: number) => void;
  setLogVersion: (fn: (v: number) => number) => void;
  roundTransition: { round: number; alpha: number; timer: number };
  narration: { text: string; displayedChars: number; timer: number; visible: boolean };
  slowMo: { active: boolean; timer: number; factor: number };
  comboMap: Record<string, { count: number; lastActor: string }>;
}

export function applyEvent(ev: AnimEvent, ctx: ApplyEventContext) {
  const { sprites, camera } = ctx;

  const pushToLog = (text: string, color: string, bold: boolean) => {
    const entry: LogEntry = { id: ctx.entryId.current++, text, color, bold };
    if (ctx.currentRound.current) {
      ctx.currentRound.current.entries.push(entry);
    } else {
      ctx.pendingEntries.push(entry);
    }
    ctx.setLogVersion(v => v + 1);
  };

  // ── Round header ──────────────────────────────────────────────────────
  if (ev.eventType === 'round_header') {
    const newRound: LogRound = { id: ctx.entryId.current++, roundNum: ev.round, entries: [] };
    ctx.logRounds.push(newRound);
    ctx.currentRound.current = newRound;
    ctx.setCurrentRound(ev.round);
    ctx.setLogVersion(v => v + 1);
    ctx.roundTransition.round = ev.round;
    ctx.roundTransition.alpha = 1;
    ctx.roundTransition.timer = 1.2;
    for (const key of Object.keys(ctx.comboMap)) delete ctx.comboMap[key];
    return;
  }

  // ── Reward / meta lines — log only ────────────────────────────────────
  if (
    ev.eventType.startsWith('reward') ||
    ['levelup', 'guild_levelup', 'guild_max_level', 'title_earned',
     'title_earned_also', 'items_unequipped', 'simulation_notice'].includes(ev.eventType)
  ) {
    pushToLog(ev.text, ev.logColor, ev.logBold);
    return;
  }

  const actor  = ev.actorKey  ? sprites.get(ev.actorKey)  : undefined;
  const target = ev.targetKey ? sprites.get(ev.targetKey) : undefined;

  // ── Narration bar ─────────────────────────────────────────────────────
  if (ev.text && !ev.eventType.startsWith('reward')) {
    ctx.narration.text = ev.text;
    ctx.narration.displayedChars = 0;
    ctx.narration.timer = 0;
    ctx.narration.visible = true;
  }

  // ── Charge event ──────────────────────────────────────────────────────
  if (actor && target && ev.eventType === 'charge') {
    if (actor.state === 'idle' || actor.state === 'recoiling') {
      actor.stateOriginX = actor.x;
      actor.stateOriginY = actor.y;
      actor.stateTargetX = actor.x + (target.x - actor.x) * 0.72;
      actor.stateTargetY = actor.y + (target.y - actor.y) * 0.72;
      actor.state = 'charging';
      actor.stateProgress = 0;
    }
  }

  // ── Damage events ─────────────────────────────────────────────────────
  if (target && DAMAGE_EVENTS.has(ev.eventType)) {
    target.flashColor = '#cc0000';
    target.flashTimer = 1.0;
    target.shakeX = (Math.random() - 0.5) * 6;
    target.shakeY = (Math.random() - 0.5) * 6;

    if (ev.damage > 0 && target.maxHp > 0) {
      target.hp = Math.max(0, target.hp - ev.damage);
      const dmgRatio = ev.damage / target.maxHp;
      const fontSize = Math.round(14 + Math.min(dmgRatio * 60, 20));

      const tKey = ev.targetKey;
      if (!ctx.comboMap[tKey]) ctx.comboMap[tKey] = { count: 0, lastActor: '' };
      const combo = ctx.comboMap[tKey];
      if (ev.actorKey === combo.lastActor) combo.count++;
      else { combo.count = 1; combo.lastActor = ev.actorKey; }

      const isCrit = ev.eventType === 'crit';
      const floatColor = isCrit ? '#ffd700' : '#ff5555';

      ctx.dmgFloats.push({
        id: ctx.dmgFloatId.current++,
        x: target.x + (Math.random() - 0.5) * 16,
        y: target.y - CIRCLE_RADIUS - 26,
        vy: -55, opacity: 1.0,
        text: String(ev.damage), color: floatColor,
        fontSize, subText: ev.bodyPart || undefined,
      });

      if (combo.count >= 2) {
        ctx.dmgFloats.push({
          id: ctx.dmgFloatId.current++,
          x: target.x + 20, y: target.y - CIRCLE_RADIUS - 40,
          vy: -30, opacity: 1.0,
          text: `x${combo.count}!`, color: '#ffaa00', fontSize: 12,
        });
      }

      ctx.particles.push(...spawnImpactBurst(target.x, target.y, isCrit ? 12 : 6, isCrit ? '#ffd700' : '#ff5555'));
      if (isCrit) camera.shakeTimer = 0.15;
    }
  }

  // ── Miss ──────────────────────────────────────────────────────────────
  if (target && (ev.eventType === 'miss' || ev.eventType === 'momentum_miss')) {
    ctx.dmgFloats.push({
      id: ctx.dmgFloatId.current++,
      x: target.x + (Math.random() - 0.5) * 12,
      y: target.y - CIRCLE_RADIUS - 26,
      vy: -45, opacity: 1.0, text: 'MISS', color: '#ff4444',
    });
  }

  // ── Dodge ─────────────────────────────────────────────────────────────
  if (target && ev.eventType === 'dodge_full') {
    ctx.dmgFloats.push({
      id: ctx.dmgFloatId.current++,
      x: target.x + (Math.random() - 0.5) * 12,
      y: target.y - CIRCLE_RADIUS - 26,
      vy: -45, opacity: 1.0, text: 'Dodge', color: '#74c0fc',
    });
  }

  // ── Graze ─────────────────────────────────────────────────────────────
  if (target && ev.eventType === 'graze') {
    ctx.dmgFloats.push({
      id: ctx.dmgFloatId.current++,
      x: target.x + (Math.random() - 0.5) * 12,
      y: target.y - CIRCLE_RADIUS - 36,
      vy: -35, opacity: 0.85, text: 'Graze', color: '#d4a060', fontSize: 13,
    });
  }

  // ── Counter Attack ────────────────────────────────────────────────────
  if (actor && ev.eventType === 'counter_dagger') {
    ctx.dmgFloats.push({
      id: ctx.dmgFloatId.current++,
      x: actor.x + (Math.random() - 0.5) * 12,
      y: actor.y - CIRCLE_RADIUS - 26,
      vy: -45, opacity: 1.0, text: 'Counter Attack', color: '#c77dff',
    });
  }

  // ── Parry / block ─────────────────────────────────────────────────────
  if (target && ['block_full', 'parry_success'].includes(ev.eventType)) {
    ctx.dmgFloats.push({
      id: ctx.dmgFloatId.current++,
      x: target.x + (Math.random() - 0.5) * 12,
      y: target.y - CIRCLE_RADIUS - 26,
      vy: -45, opacity: 1.0,
      text: ev.eventType === 'parry_success' ? 'Parry' : 'Block',
      color: '#74c0fc',
    });
    ctx.particles.push(...spawnSparkBurst(target.x, target.y));
  }

  // ── Parry fail / block fail ───────────────────────────────────────────
  if (target && ['parry_fail', 'block_fail'].includes(ev.eventType)) {
    ctx.dmgFloats.push({
      id: ctx.dmgFloatId.current++,
      x: target.x + (Math.random() - 0.5) * 12,
      y: target.y - CIRCLE_RADIUS - 26,
      vy: -40, opacity: 0.7,
      text: ev.eventType === 'parry_fail' ? 'Parry Fail' : 'Block Fail',
      color: '#88aacc', fontSize: 12,
    });
  }

  // ── Micro-charge on attack ────────────────────────────────────────────
  if (actor && target && ATTACK_EVENTS.has(ev.eventType) &&
      (actor.state === 'idle' || actor.state === 'recoiling')) {
    actor.stateOriginX = actor.x;
    actor.stateOriginY = actor.y;
    actor.stateTargetX = actor.x + (target.x - actor.x) * 0.35;
    actor.stateTargetY = actor.y + (target.y - actor.y) * 0.35;
    actor.state = 'charging';
    actor.stateProgress = 0;
  }

  // ── Dodge movement ────────────────────────────────────────────────────
  if (actor && target && ['dodge_full', 'dodge_partial'].includes(ev.eventType)) {
    if (actor.state === 'idle' || actor.state === 'recoiling') {
      actor.stateOriginX = actor.x;
      actor.stateOriginY = actor.y;
      actor.stateTargetX = actor.x + (target.x - actor.x) * 0.35;
      actor.stateTargetY = actor.y + (target.y - actor.y) * 0.35;
      actor.state = 'charging';
      actor.stateProgress = 0;
    }
    if (target.state === 'idle') {
      // Move perpendicular (up or down) instead of straight back
      const dx = target.x - actor.x;
      const dy = target.y - actor.y;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      // Perpendicular direction (rotated 90°). Pick up or down based on available space.
      const perpX = -dy / len;
      const perpY =  dx / len;
      const sign = target.y > (CANVAS_H / 2) ? -1 : 1;
      target.stateOriginX = target.x;
      target.stateOriginY = target.y;
      target.stateTargetX = target.baseX + perpX * 42 * sign;
      target.stateTargetY = target.baseY + perpY * 42 * sign;
      target.state = 'dodging';
      target.stateProgress = 0;
    }
    target.flashColor = '#74c0fc';
    target.flashTimer = 0.6;
  }

  // ── Block / parry movement ────────────────────────────────────────────
  if (target && ['block_full', 'parry_success', 'parry_partial_success', 'block_partial_success'].includes(ev.eventType)) {
    if (actor && (actor.state === 'idle' || actor.state === 'recoiling')) {
      actor.stateOriginX = actor.x;
      actor.stateOriginY = actor.y;
      actor.stateTargetX = actor.x + (target.x - actor.x) * 0.35;
      actor.stateTargetY = actor.y + (target.y - actor.y) * 0.35;
      actor.state = 'charging';
      actor.stateProgress = 0;
    }
    if (target.state === 'idle' && actor) {
      const dx = actor.x - target.x;
      const dy = actor.y - target.y;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      target.stateOriginX = target.x;
      target.stateOriginY = target.y;
      target.stateTargetX = target.baseX + (dx / len) * 18;
      target.stateTargetY = target.baseY + (dy / len) * 18;
      target.state = 'dodging';
      target.stateProgress = 0;
    }
    target.flashColor = '#aad4ff';
    target.flashTimer = 0.6;
  }

  // ── Kill / death — slow-mo + death fall ───────────────────────────────
  if (actor && ['kill', 'death', 'kill_exhaustion', 'beast_defeated'].includes(ev.eventType)) {
    // Guard: only process if not already dead
    if (actor.state !== 'dead') {
      actor.state = 'dead';
      actor.hp = 0;
      // Only trigger slow-mo if not already active (prevent stacking)
      if (!ctx.slowMo.active) {
        ctx.slowMo.active = true;
        ctx.slowMo.timer = 0.5;
        ctx.slowMo.factor = 0.3;
      }
    }
  }

  // ── Surrender ─────────────────────────────────────────────────────────
  if (actor && ev.eventType === 'surrender') {
    if (actor.state !== 'dead') actor.state = 'surrendered';
  }

  // ── Collapse / hospitalise ────────────────────────────────────────────
  if (actor && ['collapse', 'hospital_collapse'].includes(ev.eventType)) {
    if (actor.state !== 'dead') actor.state = 'collapsed';
  }

  // ── Hospital flag ─────────────────────────────────────────────────────
  if (actor && ['hospital', 'hospital_collapse'].includes(ev.eventType)) {
    actor.hospitalized = true;
    ctx.dmgFloats.push({
      id: ctx.dmgFloatId.current++,
      x: actor.x, y: actor.y - CIRCLE_RADIUS - 32,
      vy: -25, opacity: 1.0, text: '🏥 Hospitalized', color: '#ff4444', fontSize: 12,
    });
  }

  // ── Freya's Grace ─────────────────────────────────────────────────────
  if (actor && ev.eventType === 'freyas_grace') {
    actor.flashColor = '#ffffaa';
    actor.flashTimer = 1.5;
    ctx.particles.push(...spawnHealParticles(actor.x, actor.y));
  }

  // ── Axe bleed ─────────────────────────────────────────────────────────
  if (target && ev.eventType === 'axe_bleed') {
    target.flashColor = '#ff6b35'; target.flashTimer = 1.0;
    ctx.dmgFloats.push({
      id: ctx.dmgFloatId.current++, x: target.x, y: target.y - CIRCLE_RADIUS - 32,
      vy: -30, opacity: 1.0, text: 'Bleeding!', color: '#ff6b35',
    });
    ctx.particles.push(spawnBleedDrip(target.x, target.y + CIRCLE_RADIUS));
  }

  if (target && ev.eventType === 'bleed_tick') {
    ctx.particles.push(spawnBleedDrip(target.x, target.y + CIRCLE_RADIUS));
  }

  // ── Axe cleave ────────────────────────────────────────────────────────
  if (ev.eventType === 'axe_cleave') {
    const cleaveSprite = actor || target;
    if (cleaveSprite) {
      ctx.dmgFloats.push({
        id: ctx.dmgFloatId.current++,
        x: cleaveSprite.x, y: cleaveSprite.y - CIRCLE_RADIUS - 38,
        vy: -30, opacity: 1.0, text: '⚔ Cleave!', color: '#ff8844', fontSize: 14,
      });
    }
  }

  // ── Mace effects ──────────────────────────────────────────────────────
  if (target && ev.eventType === 'mace_daze') {
    target.flashColor = '#74c0fc'; target.flashTimer = 1.0;
    ctx.dmgFloats.push({
      id: ctx.dmgFloatId.current++, x: target.x, y: target.y - CIRCLE_RADIUS - 32,
      vy: -30, opacity: 1.0, text: 'Dazed!', color: '#74c0fc',
    });
  }
  if (target && ev.eventType === 'mace_daze_applied') {
    ctx.dmgFloats.push({
      id: ctx.dmgFloatId.current++, x: target.x, y: target.y - CIRCLE_RADIUS - 32,
      vy: -28, opacity: 0.85, text: 'Still Dazed!', color: '#74c0fc',
    });
  }
  if (target && ev.eventType === 'mace_stagger') {
    target.flashColor = '#f0a050'; target.flashTimer = 1.0;
    ctx.dmgFloats.push({
      id: ctx.dmgFloatId.current++, x: target.x, y: target.y - CIRCLE_RADIUS - 32,
      vy: -30, opacity: 1.0, text: 'Staggered!', color: '#f0a050',
    });
  }
  if (target && ev.eventType === 'mace_weaken') {
    target.flashColor = '#f0a050'; target.flashTimer = 1.0;
    ctx.dmgFloats.push({
      id: ctx.dmgFloatId.current++, x: target.x, y: target.y - CIRCLE_RADIUS - 32,
      vy: -30, opacity: 1.0, text: 'Weakened!', color: '#f0c050',
    });
  }
  if (actor && ev.eventType === 'mace_weaken_applied') {
    ctx.dmgFloats.push({
      id: ctx.dmgFloatId.current++, x: actor.x, y: actor.y - CIRCLE_RADIUS - 32,
      vy: -28, opacity: 0.85, text: 'Still Weakened!', color: '#f0c050',
    });
  }

  // ── Taunt ─────────────────────────────────────────────────────────────
  if (target && ev.eventType === 'taunt') {
    target.flashColor = '#ffd700'; target.flashTimer = 0.8;
    ctx.dmgFloats.push({
      id: ctx.dmgFloatId.current++, x: target.x, y: target.y - CIRCLE_RADIUS - 32,
      vy: -35, opacity: 1.0, text: 'Taunt!', color: '#ffd700',
    });
  }

  // ── Weapon break ──────────────────────────────────────────────────────
  if (target && ['parry_weapon_break', 'parry_partial_weapon_break'].includes(ev.eventType)) {
    target.flashColor = '#ffd700'; target.flashTimer = 1.5;
    // Use slot metadata to mark the correct hand as broken
    const slot = ev.slot;
    if (slot === 'offHand') {
      target.offHandBroken = true;
    } else if (slot === 'twoHand') {
      target.weaponBroken = true;
    } else {
      target.weaponBroken = true;
    }
    ctx.dmgFloats.push({
      id: ctx.dmgFloatId.current++, x: target.x, y: target.y - CIRCLE_RADIUS - 32,
      vy: -40, opacity: 1.0, text: '⚔ Weapon Break!', color: '#ffd700',
    });
    ctx.particles.push(...spawnSparkBurst(target.x, target.y));
  }

  // ── Shield break ────────────────────────────────────────────────────
  if (target && ev.eventType === 'shield_break') {
    target.flashColor = '#ffd700'; target.flashTimer = 1.5;
    target.shieldBroken = true;
    ctx.dmgFloats.push({
      id: ctx.dmgFloatId.current++, x: target.x, y: target.y - CIRCLE_RADIUS - 32,
      vy: -40, opacity: 1.0, text: '🛡 Shield Break!', color: '#ffd700',
    });
    ctx.particles.push(...spawnSparkBurst(target.x, target.y));
  }

  // ── Racial abilities ──────────────────────────────────────────────────
  if (ev.eventType.startsWith('racial_') || ev.eventType === 'freyas_grace') {
    const floatLabel = getRacialFloatLabel(ev.eventType);
    const floatSprite = actor || target;
    if (floatLabel && floatSprite) {
      ctx.dmgFloats.push({
        id: ctx.dmgFloatId.current++,
        x: floatSprite.x, y: floatSprite.y - CIRCLE_RADIUS - 32,
        vy: -30, opacity: 1.0, text: floatLabel, color: '#c77dff',
      });
      if (ev.eventType === 'racial_orc_berserk') {
        floatSprite.auraColor = 'rgb(255,50,50)'; floatSprite.auraTimer = 3.0;
      }
      if (ev.eventType === 'racial_dwarf_enhance') {
        floatSprite.auraColor = 'rgb(255,200,50)'; floatSprite.auraTimer = 3.0;
      }
    }
  }

  // ── Momentum aura ─────────────────────────────────────────────────────
  if (ev.eventType === 'momentum_hit' && actor) {
    actor.auraColor = 'rgb(100,200,255)'; actor.auraTimer = 1.5;
  }

  pushToLog(ev.text, ev.logColor, ev.logBold);
}
